/**
 * Import statement page (#36 — IOS-4.3).
 *
 * Lets users pick a CSV or PDF bank statement from the iOS document picker
 * and send it to Pazuru's /api/import endpoint for parsing and ingestion.
 *
 * On web/PWA: falls back to a standard <input type="file"> element.
 * On iOS native: uses @capawesome/capacitor-file-picker (UIDocumentPickerViewController).
 */

import { useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react'
import { pickImportFile } from '../native/filePicker'
import { apiErrorMessage } from '../lib/apiError'

type ImportStatus = 'idle' | 'picking' | 'uploading' | 'success' | 'error'

const PAZURU_IMPORT_URL = '/api/import'

export function ImportPage() {
  const [status, setStatus] = useState<ImportStatus>('idle')
  const [fileName, setFileName] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function uploadFile(file: File | Blob, name: string) {
    setStatus('uploading')
    setFileName(name)
    try {
      const form = new FormData()
      form.append('file', file, name)
      const ext = name.split('.').pop()?.toLowerCase()
      const endpoint = ext === 'pdf' ? `${PAZURU_IMPORT_URL}/pdf` : `${PAZURU_IMPORT_URL}/csv`
      const r = await fetch(endpoint, { method: 'POST', body: form })
      if (r.ok) {
        const body = await r.json() as { imported?: number; message?: string }
        setMessage(body.message ?? `Imported ${body.imported ?? 0} transactions.`)
        setStatus('success')
      } else {
        const body = await r.json().catch(() => ({}))
        setMessage(apiErrorMessage(body, `Upload failed (${r.status})`))
        setStatus('error')
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Upload failed')
      setStatus('error')
    }
  }

  async function handleNativePick() {
    setStatus('picking')
    setMessage(null)
    const picked = await pickImportFile()
    if (!picked) {
      setStatus('idle')
      return
    }
    await uploadFile(picked.blob, picked.name)
  }

  function handleWebInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    void uploadFile(file, file.name)
    // Reset so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const isNative = Capacitor.isNativePlatform()
  const busy = status === 'picking' || status === 'uploading'

  return (
    <div style={{ padding: '24px 20px', maxWidth: '480px', margin: '0 auto' }}>
      <h1
        style={{
          fontSize: '20px',
          fontWeight: 600,
          color: 'var(--color-text-primary, #F1F5F9)',
          marginBottom: '8px',
        }}
      >
        Import Statement
      </h1>
      <p
        style={{
          fontSize: '14px',
          color: 'var(--color-text-secondary, #94a3b8)',
          marginBottom: '32px',
          lineHeight: '1.5',
        }}
      >
        Pick a CSV or PDF bank statement to import transactions into Naforo.
      </p>

      {/* Status indicator */}
      {status === 'success' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 16px',
            borderRadius: '10px',
            background: 'color-mix(in srgb, #059669 15%, transparent)',
            color: '#34D399',
            fontSize: '14px',
            marginBottom: '20px',
          }}
          role="status"
        >
          <CheckCircle size={18} />
          <span>{message}</span>
        </div>
      )}
      {status === 'error' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 16px',
            borderRadius: '10px',
            background: 'color-mix(in srgb, #DC2626 15%, transparent)',
            color: '#F87171',
            fontSize: '14px',
            marginBottom: '20px',
          }}
          role="alert"
        >
          <AlertCircle size={18} />
          <span>{message}</span>
        </div>
      )}
      {status === 'uploading' && (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: '10px',
            background: 'var(--color-bg-surface)',
            color: 'var(--color-text-secondary, #94a3b8)',
            fontSize: '14px',
            marginBottom: '20px',
          }}
          role="status"
          aria-live="polite"
        >
          Uploading {fileName}…
        </div>
      )}

      {/* Pick button */}
      {isNative ? (
        <button
          onClick={handleNativePick}
          disabled={busy}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '16px',
            borderRadius: '12px',
            border: '2px dashed var(--color-bg-border)',
            background: 'var(--color-bg-surface)',
            color: busy
              ? 'var(--color-text-secondary, #94a3b8)'
              : 'var(--color-accent, #0D9488)',
            fontSize: '16px',
            fontWeight: 500,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          <Upload size={20} />
          {status === 'picking' ? 'Opening Files…' : 'Pick File from Files'}
        </button>
      ) : (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '16px',
            borderRadius: '12px',
            border: '2px dashed var(--color-bg-border)',
            background: 'var(--color-bg-surface)',
            color: 'var(--color-accent, #0D9488)',
            fontSize: '16px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FileText size={20} />
          Choose CSV or PDF
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.pdf,.xls,.xlsx"
            style={{ display: 'none' }}
            onChange={handleWebInput}
            disabled={busy}
          />
        </label>
      )}

      <p
        style={{
          marginTop: '16px',
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}
      >
        Supported formats: CSV, PDF, XLS, XLSX
      </p>
    </div>
  )
}
